"use client";

import Link from "next/link";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { getTextColorFromBg } from "@/lib/colors";
import type { HeaderProps } from "@/components/headers";
import { Button } from "@/components/ui/button";
import { getBtnVariant } from "@/lib/background";
import { renderIcon } from "@/lib/icon";

export default function HeaderWithTopbar({ data }: HeaderProps) {
  const { logo, menu = [], cta, topbar, layout = {}, background } = data;
  const [open, setOpen] = useState(false);

  const isSticky = layout?.sticky;
  const isTransparent = layout?.transparent;
  const isColor = background?.type === "color";
  const bgStyle = isColor ? { backgroundColor: background.value } : {};
  const bgClass = isTransparent ? "bg-transparent" : !isColor ? "bg-white" : "";
  const textClass = isColor
    ? getTextColorFromBg(background.value)
    : isTransparent
      ? "text-inherit"
      : "text-foreground";

  return (
    <header
      className={`w-full z-50 ${isSticky ? "sticky top-0" : ""}`}
      style={bgStyle}
    >
      {/* Topbar */}
      {topbar?.text && (
        <div className="w-full bg-primary text-primary-foreground text-sm py-2 px-4 text-center">
          {topbar.link ? (
            <Link href={topbar.link} className="hover:underline">
              {topbar.text}
            </Link>
          ) : (
            topbar.text
          )}
        </div>
      )}

      {/* Main Header */}
      <div className={`${bgClass} ${textClass} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          {/* Logo */}
          <Link href="/">
            <img
              src={logo?.src}
              alt={logo?.alt || "Logo"}
              className="h-8 w-auto"
            />
          </Link>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-6">
            {menu.map((item, idx) => (
              <Link
                key={idx}
                href={item.href || "#"}
                className="inline-flex items-center gap-2 text-sm font-medium hover:text-primary"
              >
                {item.icon && renderIcon({ icon: item.icon, size: 18 })}
                {item.label}
              </Link>
            ))}
            {cta?.href && cta?.text && (
              <Link href={cta.href}>
                <Button variant={getBtnVariant(cta.style)} className="ml-4">
                  {cta.text}
                </Button>
              </Link>
            )}
          </nav>

          {/* Mobile Toggle */}
          <button onClick={() => setOpen(!open)} className="md:hidden">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Dropdown */}
        {open && (
          <div className="md:hidden bg-popover border-t">
            <div className="flex flex-col px-4 py-2 space-y-2">
              {menu.map((item, idx) => (
                <Link
                  key={idx}
                  href={item.href ?? "/"}
                  className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  {item.icon && renderIcon({ icon: item.icon, size: 18 })}
                  {item.label}
                </Link>
              ))}
              {cta?.href && cta?.text && (
                <Link href={cta.href}>
                  <Button variant={getBtnVariant(cta.style)} className="mt-2">
                    {cta.text}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
